import React, { useState } from 'react';
import { Col, Container, Row } from 'reactstrap';
import { Link } from 'react-router-dom';

const features = {
  free: [
    {
      icon: 'uil-bookmark',
      title: 'Save up to 18 words',
      desc: 'Build your starter vocabulary list',
    },
    {
      icon: 'uil-search',
      title: 'Word discovery & search',
      desc: 'Explore definitions and examples',
    },
    {
      icon: 'uil-graduation-cap',
      title: 'Quiz practice',
      desc: "Test what you've learned",
    },
    {
      icon: 'uil-sun',
      title: 'Word of the day',
      desc: 'Daily vocabulary boost',
    },
  ],
  pro: [
    {
      icon: 'uil-bookmark-full',
      title: 'Unlimited saved words',
      desc: 'No cap — save every word you encounter',
    },
    {
      icon: 'uil-robot',
      title: 'Unlimited AI writing help',
      desc: 'Fix, rewrite & generate replies instantly',
    },
    {
      icon: 'uil-layer-group',
      title: 'Save a word pack',
      desc: 'Save a curated topic collection from Discover',
    },
  ],
};

const FeatureRow = ({ icon, title, desc }) => (
  <li className="d-flex align-items-start mb-3">
    <div
      className="flex-shrink-0 rounded-3 d-flex align-items-center justify-content-center me-3"
      style={{
        width: 40,
        height: 40,
        backgroundColor: 'rgba(121, 82, 179, 0.1)',
      }}
    >
      <i className={`uil ${icon} text-primary`} style={{ fontSize: 18 }}></i>
    </div>
    <div>
      <p className="mb-0 fw-semibold text-dark" style={{ fontSize: 14 }}>
        {title}
      </p>
      <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
        {desc}
      </p>
    </div>
  </li>
);

const Pricing = ({}) => {
  const [activeTab, setActiveTab] = useState('monthly');

  return (
    <React.Fragment>
      <section style={{ marginTop: 100, marginBottom: 100 }}>
        <Container>
          {/* Header */}
          <Row className="justify-content-center mb-4">
            <Col lg={6} md={8} xs={12} className="text-center px-4">
              <h4 className="title mb-3">Choose your plan</h4>
              <p className="text-muted">
                Start free, then unlock unlimited words and AI writing tools when you're ready.
              </p>
            </Col>
          </Row>

          {/* Toggle */}
          <Row className="justify-content-center mb-5">
            <Col xs="auto">
              <div
                className="d-flex align-items-center border rounded-pill py-1 px-2"
                style={{ gap: 4 }}
              >
                <button
                  onClick={() => setActiveTab('monthly')}
                  className={`btn btn-sm rounded-pill px-3 py-1 ${
                    activeTab === 'monthly'
                      ? 'btn-primary'
                      : 'btn-link text-muted text-decoration-none'
                  }`}
                  style={{ fontSize: 14 }}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setActiveTab('yearly')}
                  className={`btn btn-sm rounded-pill px-3 py-1 d-flex align-items-center ${
                    activeTab === 'yearly'
                      ? 'btn-primary'
                      : 'btn-link text-muted text-decoration-none'
                  }`}
                  style={{ fontSize: 14, gap: 6 }}
                >
                  Yearly
                  <span
                    className="badge rounded-pill bg-success ms-1"
                    style={{ fontSize: 11 }}
                  >
                    30% Off
                  </span>
                </button>
              </div>
            </Col>
          </Row>

          {/* Cards */}
          <Row className="justify-content-center align-items-stretch g-4 px-2 px-md-0">
            {/* Free */}
            <Col lg={4} md={6} xs={12}>
              <div className="card h-100 shadow-sm rounded-4 border-0 bg-light">
                <div className="card-body p-4 d-flex flex-column">
                  <h6
                    className="fw-bold text-uppercase text-primary mb-2"
                    style={{ letterSpacing: '0.08em' }}
                  >
                    Free
                  </h6>
                  <div className="d-flex align-items-end mb-1">
                    <span className="h5 mb-1 text-dark fw-bold">$</span>
                    <span
                      className="fw-bold text-dark lh-1 mx-1"
                      style={{ fontSize: 48 }}
                    >
                      0
                    </span>
                    <span className="h6 text-muted mb-1">/forever</span>
                  </div>
                  <p className="text-muted mb-4" style={{ fontSize: 13 }}>
                    No credit card needed
                  </p>

                  <ul className="list-unstyled mb-0 ps-0 flex-grow-1">
                    {features.free.map((f) => (
                      <FeatureRow key={f.title} {...f} />
                    ))}
                  </ul>

                  <a
                    href="https://chrome.google.com/webstore/detail/highvocab-vocabulary-buil/nmalijlbcckplnamalenpdaafacfeemd?hl=en"
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-primary mt-4 w-100 rounded-pill"
                  >
                    Get started free
                  </a>
                </div>
              </div>
            </Col>

            {/* Pro */}
            <Col lg={4} md={6} xs={12}>
              <div
                className="card h-100 shadow rounded-4 border-0"
                style={{ outline: '2px solid #7952b3' }}
              >
                <div className="card-body p-4 d-flex flex-column">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6
                      className="fw-bold text-uppercase text-primary mb-0"
                      style={{ letterSpacing: '0.08em' }}
                    >
                      Pro
                    </h6>
                    <span
                      className="badge rounded-pill text-white px-3 py-2"
                      style={{ backgroundColor: '#7952b3', fontSize: 11 }}
                    >
                      Most Popular
                    </span>
                  </div>
                  <div className="d-flex align-items-end mb-1">
                    <span className="h5 mb-1 text-dark fw-bold">$</span>
                    <span
                      className="fw-bold text-dark lh-1 mx-1"
                      style={{ fontSize: 48 }}
                    >
                      {activeTab === 'monthly' ? '2.99' : '25'}
                    </span>
                    <span className="h6 text-muted mb-1">
                      {activeTab === 'monthly' ? '/mo' : '/yr'}
                    </span>
                  </div>
                  <p className="text-muted mb-4" style={{ fontSize: 13 }}>
                    {activeTab === 'yearly'
                      ? 'Billed annually — save 30%'
                      : 'Cancel anytime'}
                  </p>

                  <ul className="list-unstyled mb-0 ps-0 flex-grow-1">
                    <li className="mb-3">
                      <small
                        className="text-uppercase fw-semibold text-muted"
                        style={{ letterSpacing: '0.06em', fontSize: 11 }}
                      >
                        Everything in Free, plus:
                      </small>
                    </li>
                    {features.pro.map((f) => (
                      <FeatureRow key={f.title} {...f} />
                    ))}
                  </ul>

                  <Link
                    to="/payment"
                    target="_blank"
                    className="btn btn-primary mt-4 w-100 rounded-pill"
                  >
                    Upgrade now
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </React.Fragment>
  );
};

export default Pricing;
